import { fileWithResolvedMimeType } from '@affine/core/utils/blob-file-type';
import { opfsBlobCache } from '@affine/core/utils/opfs-blob-cache';
import { workspaceBlobQuotaQuery } from '@affine/graphql';
import {
  resolveCloudHttpBaseUrl,
  resolveCloudHttpEndpoint,
  tusUploadRetryOptions,
} from '@affine/nbstore/cloud';
import type { UploadProgressCallback } from '@blocksuite/affine/shared/services';
import { streamingSha256 } from '@blocksuite/affine/shared/utils';
import * as tus from 'tus-js-client';

import { workspaceApiFetch } from '../../cloud';
import {
  workspaceBlobCacheToken,
  type WorkspaceBlobContext,
  WorkspaceBlobLocalStorageUnavailableError,
  workspaceBlobNamespace,
} from './blob-cache';
import {
  type DemoSampleSelection,
  isPreparedDemoSample,
  materializeDemoSample,
} from './demo-input-policy';

const TUS_CONFIG_CACHE_MS = 120_000;

interface TusUploadConfig {
  enabled: boolean;
  path: string | null;
}

interface CachedTusUploadConfig {
  config: TusUploadConfig;
  expiresAt: number;
}

export interface UploadWorkspaceFileViaTusOptions {
  workspaceId: string;
  context: WorkspaceBlobContext;
  file: File;
  expectedBlobKey?: string;
  docId?: string;
  onProgress?: UploadProgressCallback;
  serverBaseUrl?: string;
  tusPath?: string;
  externalDocAccess?: boolean;
}

const tusConfigCache = new Map<string, CachedTusUploadConfig>();

export class WorkspaceBlobHashMismatchError extends Error {}

function demoSelectionForKey(
  blobKey: string
): Extract<DemoSampleSelection, { sha256: string }> {
  if (!/^[A-Za-z0-9_-]{43}$/.test(blobKey)) {
    throw new Error('Only prepared demo files may be staged');
  }
  const decoded = atob(blobKey.replace(/-/g, '+').replace(/_/g, '/'));
  if (decoded.length !== 32) throw new Error('Invalid prepared demo file key');
  return {
    sha256: [...decoded]
      .map(char => char.charCodeAt(0).toString(16).padStart(2, '0'))
      .join(''),
  };
}

export function isWorkspaceBlobHashMismatchError(
  error: unknown
): error is WorkspaceBlobHashMismatchError {
  return error instanceof WorkspaceBlobHashMismatchError;
}

function normalizeServerBaseUrl(serverBaseUrl?: string): string {
  return resolveCloudHttpBaseUrl(serverBaseUrl ?? location.origin);
}

async function getWorkspaceTusConfig(
  workspaceId: string,
  serverBaseUrl: string
): Promise<TusUploadConfig> {
  const cacheKey = `${serverBaseUrl}:${workspaceId}`;
  const cached = tusConfigCache.get(cacheKey);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.config;
  }

  // Discovery failures deliberately bubble without populating the cache.
  // Reconnect retries must discover the current server capability immediately.
  const response = await workspaceApiFetch(
    resolveCloudHttpEndpoint(serverBaseUrl, '/graphql'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        query: workspaceBlobQuotaQuery.query,
        variables: { id: workspaceId },
      }),
    },
    { workspaceId, category: 'workspace-api' }
  );
  const json = await response.json();
  const quota = json.data?.workspace?.quota;
  const config = {
    enabled: quota?.tusUploadEnabled ?? false,
    path: quota?.tusUploadPath ?? null,
  };
  tusConfigCache.set(cacheKey, {
    config,
    expiresAt: Date.now() + TUS_CONFIG_CACHE_MS,
  });
  return config;
}

function uploadViaTus({
  workspaceId,
  file,
  expectedBlobKey,
  tusPath,
  serverBaseUrl,
  onProgress,
  docId,
  externalDocAccess,
  context,
}: UploadWorkspaceFileViaTusOptions & {
  tusPath: string;
  serverBaseUrl: string;
}): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    let blobKey: string | null = null;

    const upload = new tus.Upload(file, {
      endpoint: resolveCloudHttpEndpoint(serverBaseUrl, tusPath),
      ...tusUploadRetryOptions(),
      metadata: {
        workspaceId,
        uploadMode: 'content-addressed',
        mimeType: file.type || 'application/octet-stream',
        ...(docId && { docId }),
        ...(externalDocAccess && { externalDocAccess: 'true' }),
      },
      onBeforeRequest: req => {
        context.assertActive();
        const xhr = req.getUnderlyingObject();
        if (xhr && 'withCredentials' in xhr) {
          (xhr as XMLHttpRequest).withCredentials = true;
        }
      },
      onAfterResponse: (_req, res) => {
        const key = res.getHeader('X-Blob-Key');
        if (key) {
          blobKey = key;
        }
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        onProgress?.(bytesUploaded, bytesTotal);
      },
      onError: error => reject(error),
      onSuccess: () => {
        if (!blobKey) {
          reject(new Error('Server did not return X-Blob-Key header'));
          return;
        }
        if (expectedBlobKey && blobKey !== expectedBlobKey) {
          reject(
            new WorkspaceBlobHashMismatchError(
              `TUS content-addressed upload returned mismatched blob key ${blobKey}`
            )
          );
          return;
        }
        resolve(blobKey);
      },
    });

    upload.start();
  });
}

async function uploadWorkspaceFileViaTusInternal(
  {
    workspaceId,
    file,
    expectedBlobKey,
    docId,
    onProgress,
    serverBaseUrl,
    tusPath,
    externalDocAccess,
    context,
  }: UploadWorkspaceFileViaTusOptions,
  reuseStagedCache: boolean
): Promise<string | null> {
  context.assertActive();
  const namespace = workspaceBlobNamespace(context.local);
  if (isPreparedDemoSample(file)) {
    const selection: DemoSampleSelection = expectedBlobKey
      ? demoSelectionForKey(expectedBlobKey)
      : demoSelectionForKey(await streamingSha256(file));
    context.assertActive();
    const { blobKey } = await materializeDemoSample(workspaceId, selection);
    context.assertActive();
    if (expectedBlobKey && blobKey !== expectedBlobKey) {
      throw new WorkspaceBlobHashMismatchError(
        'Prepared demo file key mismatch'
      );
    }
    if (!reuseStagedCache) {
      if (!opfsBlobCache.available) {
        throw new WorkspaceBlobLocalStorageUnavailableError();
      }
      const cached = await opfsBlobCache.set(namespace, blobKey, file, {
        mime: file.type,
        size: file.size,
        source: 'upload',
        cacheToken: workspaceBlobCacheToken(blobKey, file.size),
        uploadState: 'synced',
      });
      if (!cached) throw new WorkspaceBlobLocalStorageUnavailableError();
    } else {
      await opfsBlobCache.setUploadState(namespace, blobKey, 'synced');
    }
    context.assertActive();
    onProgress?.(file.size, file.size);
    return blobKey;
  }
  if (!opfsBlobCache.available) {
    throw new WorkspaceBlobLocalStorageUnavailableError();
  }

  const normalizedServerBaseUrl = normalizeServerBaseUrl(serverBaseUrl);
  const config = tusPath
    ? { enabled: true, path: tusPath }
    : await getWorkspaceTusConfig(workspaceId, normalizedServerBaseUrl);
  if (!config.enabled || !config.path) return null;

  const uploadFile = await fileWithResolvedMimeType(file);
  context.assertActive();
  const blobKey = await uploadViaTus({
    workspaceId,
    file: uploadFile,
    expectedBlobKey,
    docId,
    onProgress,
    tusPath: config.path,
    serverBaseUrl: normalizedServerBaseUrl,
    externalDocAccess,
    context,
  });

  context.assertActive();
  const cached = reuseStagedCache
    ? await opfsBlobCache.setUploadState(namespace, blobKey, 'synced')
    : await opfsBlobCache.set(namespace, blobKey, uploadFile, {
        mime: uploadFile.type || 'application/octet-stream',
        size: uploadFile.size,
        source: 'upload',
        cacheToken: workspaceBlobCacheToken(blobKey, uploadFile.size),
        uploadState: 'synced',
      });
  if (!cached) {
    throw new WorkspaceBlobLocalStorageUnavailableError();
  }

  context.assertActive();
  return blobKey;
}

export async function uploadWorkspaceFileViaTus(
  options: UploadWorkspaceFileViaTusOptions
): Promise<string | null> {
  return uploadWorkspaceFileViaTusInternal(options, false);
}

export interface StageWorkspaceFileOptions {
  workspaceId: string;
  context: WorkspaceBlobContext;
  file: File;
}

/**
 * Persist upload bytes in the canonical OPFS cache under their content hash.
 * This is the sole durable binary copy used by offline rendering and retries.
 */
export async function stageWorkspaceFile({
  workspaceId,
  context,
  file,
}: StageWorkspaceFileOptions): Promise<{
  blobKey: string;
  file: File;
  alreadySynced: boolean;
}> {
  context.assertActive();
  const namespace = workspaceBlobNamespace(context.local);
  const preparedDemoSample = isPreparedDemoSample(file);
  if (!opfsBlobCache.available) {
    throw new WorkspaceBlobLocalStorageUnavailableError();
  }

  const uploadFile = await fileWithResolvedMimeType(file);
  const blobKey = await streamingSha256(uploadFile);
  if (preparedDemoSample) {
    context.assertActive();
    const materialized = await materializeDemoSample(workspaceId, {
      sha256: demoSelectionForKey(blobKey).sha256,
    });
    if (materialized.blobKey !== blobKey) {
      throw new WorkspaceBlobHashMismatchError(
        'Prepared demo file key mismatch'
      );
    }
  }
  context.assertActive();
  const cached = await opfsBlobCache.set(namespace, blobKey, uploadFile, {
    mime: uploadFile.type || 'application/octet-stream',
    size: uploadFile.size,
    source: 'upload',
    cacheToken: workspaceBlobCacheToken(blobKey, uploadFile.size),
    uploadState: preparedDemoSample ? 'synced' : 'pending',
  });
  if (!cached) {
    throw new WorkspaceBlobLocalStorageUnavailableError();
  }

  context.assertActive();
  return { blobKey, file: uploadFile, alreadySynced: preparedDemoSample };
}

export async function uploadStagedWorkspaceFileViaTus(
  options: Omit<
    UploadWorkspaceFileViaTusOptions,
    'file' | 'expectedBlobKey'
  > & {
    blobKey: string;
  }
): Promise<string | null> {
  options.context.assertActive();
  const file = await opfsBlobCache.getCompleteFile(
    workspaceBlobNamespace(options.context.local),
    options.blobKey
  );
  options.context.assertActive();
  if (!file) {
    throw new WorkspaceBlobLocalStorageUnavailableError();
  }
  const { blobKey, ...uploadOptions } = options;
  return uploadWorkspaceFileViaTusInternal(
    {
      ...uploadOptions,
      file,
      expectedBlobKey: blobKey,
    },
    true
  );
}
